import { type Message } from "@/shared/reader";
import { type TopicData } from "@/shared/state/visualization";
import { type MessagePayload } from "@/shared/webworker";

export const SOURCE = "RawMessageService";

/** Inclusive index range into the time index (same shape as LogPanel's IndexRange) */
export interface IndexRange {
  start: number;
  end: number;
}

export enum RawMessageCommand {
  Init = "init",
  SetState = "set-state",
  /** Fetch messages for a range of index positions in the time index */
  GetMessages = "get-messages",
  Dispose = "dispose",
}

export enum RawMessageCommandResponse {
  /** Batch of messages loaded for a requested index range */
  MessagesLoaded = "messages-loaded",
}

export interface RawMessageCommandPayloadMap {
  [RawMessageCommand.Init]: undefined;
  [RawMessageCommand.SetState]: TopicData[];
  /** Range of index positions to fetch messages for (inclusive) */
  [RawMessageCommand.GetMessages]: IndexRange;
  [RawMessageCommand.Dispose]: undefined;
}

export interface RawMessageCommandResponsePayloadMap {
  /** Array of messages corresponding to the requested index range */
  [RawMessageCommandResponse.MessagesLoaded]: Message[];
}

// Type guard functions (following Log Panel / Map Panel pattern)
export function isInitCommand(
  event: MessageEvent<MessagePayload>,
): event is MessageEvent<
  MessagePayload<
    RawMessageCommand,
    RawMessageCommandPayloadMap[RawMessageCommand.Init]
  >
> {
  return event.data.type === RawMessageCommand.Init;
}

export function isSetStateCommand(
  event: MessageEvent<MessagePayload>,
): event is MessageEvent<
  MessagePayload<
    RawMessageCommand,
    RawMessageCommandPayloadMap[RawMessageCommand.SetState]
  >
> {
  return event.data.type === RawMessageCommand.SetState;
}

export function isGetMessagesCommand(
  event: MessageEvent<MessagePayload>,
): event is MessageEvent<
  MessagePayload<
    RawMessageCommand,
    RawMessageCommandPayloadMap[RawMessageCommand.GetMessages]
  >
> {
  return event.data.type === RawMessageCommand.GetMessages;
}

export function isDisposeCommand(
  event: MessageEvent<MessagePayload>,
): event is MessageEvent<
  MessagePayload<
    RawMessageCommand,
    RawMessageCommandPayloadMap[RawMessageCommand.Dispose]
  >
> {
  return event.data.type === RawMessageCommand.Dispose;
}
